var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as O from 'fp-ts/Option';
import * as Rx from '../fp-ts-rxjs/Observable';
import * as RxRO from '../fp-ts-rxjs/ReaderObservable';
import { flow, identity, pipe } from '../fp-ts/function';
import * as RR from '../fp-ts/ReadonlyRecord';
import * as s from '../fp-ts/string';
export var empty = RxRO.zero;
export var zero = function () {
    return function () {
        return empty();
    };
};
// -----------------------------------------------------------------------------
// Action Guards
// -----------------------------------------------------------------------------
export var ofType = function () {
    var creators = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        creators[_i] = arguments[_i];
    }
    return Rx.filter(function (a) { return creators.some(function (creator) { return a.type === creator.type; }); });
};
export var ofTypeP = function () {
    var _a = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        _a[_i] = arguments[_i];
    }
    var a = _a[0], rest = _a.slice(1);
    return flow(ofType.apply(void 0, __spreadArray([a], rest, false)), Rx.map(function (action) { return action.payload; }));
};
// -----------------------------------------------------------------------------
// Combination
// -----------------------------------------------------------------------------
export var getMonoid = function () {
    var M = RxRO.Merge.getMonoid();
    return {
        concat: function (a, b) { return function (action$, state$) { return M.concat(a(action$, state$), b(action$, state$)); }; },
        empty: function () { return M.empty; }
    };
};
export var concatW = function (a) {
    return function (b) {
        return getMonoid().concat(a, b);
    };
};
/**
 * Combines Epics of an Epic map which matches the structure of a given state S.
 *
 * ### Example
 * ```ts
 * import * as Foo from './Foo'
 * import * as Bar from './Bar'
 *
 * interface State {
 *  foo: Foo.State
 *  bar: Bar.State
 * }
 *
 * const Epic = combine<State>()({
 *   foo: Foo.Epic,
 *   bar: Bar.Epic
 * })
 * ```
 */
export var combine = function () {
    return function (m) {
        return pipe(
        // we need to reduce information here to be compliant with a record
        m, RR.foldMapWithIndex(s.Ord)(getMonoid())(function (key, epic) {
            return pipe(epic, O.fromPredicate(function (epic) { return typeof epic === 'function'; }), O.map(contramapState(function (state) { return state[key]; })), O.altW(function () {
                return pipe(epic, 
                // Although it could be a deep record we tell here that it is a
                // flat record (of only Epics) as a workaround or otherwise TS
                // will go infinitely deep for type instatiation causing an error
                O.fromPredicate(function (epic) { return typeof epic === 'object'; }), O.map(combine()));
            }), 
            // fallback will be never reached
            O.getOrElseW(function () { return empty; }));
        }));
    };
};
// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------
/**
 * Map over the state observable of an Epic.
 */
export var mapState = function (f) {
    return function (fa) {
        return function (action$, state$) {
            return fa(action$, f(state$));
        };
    };
};
/**
 * Contramap of the state of an Epic.
 */
export var contramapState = function (f) {
    return function (fa) {
        return function (action$, state$) {
            return fa(action$, pipe(state$, Rx.map(f)));
        };
    };
};
/**
 * A helper to create an epic which can infer the reader type. The state type
 * must be provided.
 */
export var make = function () { return identity; };
/**
 * A helper to create an epic like `make` but which focuses on the state
 * observable.
 */
export var makeState = function () {
    return function (f) {
        return function (_, state$) {
            return f(state$);
        };
    };
};
